import { Text } from "@ttc3k/trekker";
import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CopyToClipboard } from "shared/components/molecules";
import { Box, Flex, styled } from "styled-system/jsx";

type DeveloperZoneExamplesProps = {
  endpoint: string;
  token: string;
  organizationId?: string;
};

type ExampleKey = "operatorById" | "eventById" | "eventMany";

const maskToken = (token: string) => {
  if (token.length <= 18) return token;
  return `${token.slice(0, 10)}...${token.slice(-8)}`;
};

const buildCurlExample = ({
  endpoint,
  token,
  organizationId,
  query,
  variablesJson,
}: {
  endpoint: string;
  token: string;
  organizationId?: string;
  query: string;
  variablesJson: string;
}) => {
  const headers = [
    `  -H 'content-type: application/json' \\`,
    `  -H 'authorization: Bearer ${token}' \\`,
  ];

  if (organizationId) {
    const metadata = JSON.stringify({
      entityId: organizationId,
      type: "ORGANIZATION",
    });
    headers.push(`  -H 'metadata: ${metadata}' \\`);
  }

  const body = JSON.stringify({ query, variables: JSON.parse(variablesJson) });
  return [
    `curl '${endpoint}' \\`,
    ...headers,
    `  --data-raw '${body.replace(/'/g, "'\\''")}'`,
  ].join("\n");
};

const EXAMPLES: Array<{
  key: ExampleKey;
  query: string;
  variables: string;
}> = [
  {
    key: "operatorById",
    query:
      "query OperatorById($id: MongoID!) {\n" +
      "  operatorById(_id: $id) {\n" +
      "    _entityId\n" +
      "    name { en fr es }\n" +
      "  }\n" +
      "}",
    variables: '{"id":"REPLACE_WITH_OPERATOR_ID"}',
  },
  {
    key: "eventById",
    query:
      "query EventById($id: MongoID!) {\n" +
      "  eventById(_id: $id) {\n" +
      "    _entityId\n" +
      "    title { en fr es }\n" +
      "  }\n" +
      "}",
    variables: '{"id":"REPLACE_WITH_EVENT_ID"}',
  },
  {
    key: "eventMany",
    query:
      "query EventMany($limit: Int) {\n" +
      "  eventMany(limit: $limit) {\n" +
      "    _id\n" +
      "    title { en fr es }\n" +
      "  }\n" +
      "}",
    variables: '{"limit":10}',
  },
];

export const DeveloperZoneExamples = ({
  endpoint,
  token,
  organizationId,
}: DeveloperZoneExamplesProps) => {
  const { t } = useTranslation("core", {
    keyPrefix: "DEVELOPER_ZONE.EXAMPLES",
  });
  const [activeKey, setActiveKey] = useState<ExampleKey>("operatorById");

  const activeExample = useMemo(
    () => EXAMPLES.find(ex => ex.key === activeKey) ?? EXAMPLES[0],
    [activeKey],
  );

  const curl = useMemo(
    () =>
      buildCurlExample({
        endpoint,
        token,
        organizationId,
        query: activeExample.query,
        variablesJson: activeExample.variables,
      }),
    [endpoint, token, organizationId, activeExample],
  );
  const displayCurl = useMemo(
    () => curl.replace(token, maskToken(token)),
    [curl, token],
  );

  return (
    <Flex flexDir="column" gap="200" w="full">
      <Flex gap="100" flexWrap="wrap">
        {EXAMPLES.map(ex => (
          <TabButton
            key={ex.key}
            type="button"
            data-active={ex.key === activeKey}
            onClick={() => setActiveKey(ex.key)}
          >
            {t(`TABS.${ex.key.toUpperCase()}`, ex.key)}
          </TabButton>
        ))}
      </Flex>
      <Text color="text.light" fontSize="sm">
        {t("HINT")}
      </Text>
      <Box position="relative">
        <styled.pre
          fontFamily="mono"
          fontSize="xs"
          bg="bg.subtlest"
          border="1px solid"
          borderColor="border.light"
          borderRadius="100"
          p="200"
          overflowX="auto"
          whiteSpace="pre"
          color="text.dark"
        >
          {displayCurl}
        </styled.pre>
        <Box position="absolute" top="100" right="100">
          <CopyToClipboard iconOnly text={curl} iconCopyColor="icon.mid" />
        </Box>
      </Box>
    </Flex>
  );
};

const TabButton = styled("button", {
  base: {
    px: "200",
    py: "100",
    borderRadius: "full",
    bg: "bg.subtlest",
    border: "1px solid",
    borderColor: "border.light",
    cursor: "pointer",
    fontSize: "sm",
    color: "text.mid",
    _hover: { bg: "bg.subtle" },
    "&[data-active='true']": {
      bg: "bg.accent.blue.dark",
      color: "white",
      borderColor: "bg.accent.blue.dark",
    },
  },
});
